import styles from './styles.scss';
import {
  ProductsServicesTab,
  CategoriesTab,
  ChargeCodesTab,
  CostOffsetCodesTab,
  ExceptionsTab,
} from './tabs';
import {
  useFlag,
  useUserDataContext,
  useChannelsContext,
} from 'lane-shared/hooks';
import { FeatureFlag } from 'constants-flags';
import { TabStrip } from 'components/general';
import { AdminPage, PageHeader } from 'components/layout';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryString } from 'hooks';
import { PERMISSION_KEYS } from 'constants-permissions';
import { hasPermission } from 'lane-shared/helpers';

export enum ProductsServicesTabs {
  ProductsAndServices = 'productsAndServices',
  Categories = 'categories',
  Exceptions = 'exceptions',
  ChargeCodes = 'chargeCodes',
  CostOffsetCodes = 'costOffsetCodes',
}

type Props = {
  breadcrumbs?: {
    label: string;
    url?: string;
  }[];
  productFormRoute?: string;
  categoryFormRoute?: string;
  chargeCodeFormRoute?: string;
  costOffsetCodeFormRoute?: string;
  exceptionFormRoute?: string;
};

export function ProductsServices({
  breadcrumbs,
  productFormRoute,
  categoryFormRoute,
  chargeCodeFormRoute,
  costOffsetCodeFormRoute,
  exceptionFormRoute,
}: Props) {
  const [selectedTab, setSelectedTab] = useQueryString<{
    tab: ProductsServicesTabs;
  }>({
    tab: ProductsServicesTabs.ProductsAndServices,
  });

  const { t } = useTranslation();
  const { user } = useUserDataContext();
  const { primaryChannel } = useChannelsContext();

  const canManageChargeCode =
    user?.isSuperUser ||
    hasPermission(
      user?.roles,
      [PERMISSION_KEYS.PERMISSION_CHARGE_CODE_MANAGE],
      primaryChannel?._id
    );

  const canManageProductsAndServices =
    user?.isSuperUser ||
    hasPermission(
      user?.roles,
      [PERMISSION_KEYS.PERMISSION_PRODUCTS_MANAGE],
      primaryChannel?._id
    );

  const canManageExceptions =
    user?.isSuperUser ||
    hasPermission(
      user?.roles,
      [PERMISSION_KEYS.PERMISSION_PRODUCT_EXCEPTIONS_MANAGE],
      primaryChannel?._id
    );

  const canManageCostOffsetCodes = 
    user?.isSuperUser ||
    hasPermission(
      user?.roles,
      [PERMISSION_KEYS.PERMISSION_COST_OFFSET_CODES_MANAGE],
      primaryChannel?._id
    );

  const tabs = [
    {
      value: ProductsServicesTabs.ProductsAndServices,
      label: 'abp.productsServices.tab.productsAndServices',
    },
    {
      value: ProductsServicesTabs.Categories,
      label: 'abp.productsServices.tab.categories',
    },
  ];

  tabs.push({
    value: ProductsServicesTabs.Exceptions,
    label: 'abp.productsServices.tab.exceptions',
  });

  const YardiChargeCodeFlag = useFlag(FeatureFlag.YardiChargeCode, false);
  const ManualBillingReportsFlag = useFlag(
    FeatureFlag.ManualBillingReports,
    false
  );

  const tabComponents: Partial<Record<ProductsServicesTabs, React.ReactNode>> = {
    [ProductsServicesTabs.ProductsAndServices]: (
      <ProductsServicesTab
        formRoute={productFormRoute}
        canManage={canManageProductsAndServices}
      />
    ),
    [ProductsServicesTabs.Categories]: (
      <CategoriesTab
        formRoute={categoryFormRoute}
        canManage={canManageProductsAndServices}
      />
    ),
    [ProductsServicesTabs.Exceptions]: (
      <ExceptionsTab
        formRoute={exceptionFormRoute}
        canManage={canManageExceptions}
      />
    ),
    ...(YardiChargeCodeFlag && {
      [ProductsServicesTabs.ChargeCodes]: (
        <ChargeCodesTab
          formRoute={chargeCodeFormRoute}
          canManage={canManageChargeCode}
        />
      ),
    }),
    ...(ManualBillingReportsFlag && {
      [ProductsServicesTabs.CostOffsetCodes]: (
        <CostOffsetCodesTab
          formRoute={costOffsetCodeFormRoute}
          canManage={canManageCostOffsetCodes}
        />
      ),
    }),
  };

  if (YardiChargeCodeFlag) {
    tabs.push({
      value: ProductsServicesTabs.ChargeCodes,
      label: 'abp.productsServices.tab.chargeCodes',
    });
  }

  if (ManualBillingReportsFlag) {
    tabs.push({
      value: ProductsServicesTabs.CostOffsetCodes,
      label: 'abp.productsServices.tab.costOffsetCodes',
    });
  }

  return (
    <AdminPage>
      <PageHeader
        header={t('abp.productsServices.title')}
        headerLevel="h3"
        breadcrumbs={breadcrumbs}
        externalPadding={[0, 0]}
      />
      <TabStrip
        tabs={tabs}
        fullWidth
        selected={{ value: selectedTab.tab }}
        onSelectTab={tab =>
          setSelectedTab({ tab: tab.value as ProductsServicesTabs })
        }
        className={styles.TabStrip}
      />
      {tabComponents[selectedTab.tab] ??
        tabComponents[ProductsServicesTabs.ProductsAndServices]}
    </AdminPage>
  );
}
